import { useEffect, useMemo, useState } from "react";
import { Inbox, Mail, MailOpen, RefreshCw, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";
import { useAuth } from "@/contexts/AuthContext";

const formatDate = value => {
  if (!value) return "Date inconnue";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date inconnue";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(date);
};

export default function GestionMessagerie() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/contacts`);
      const items = Array.isArray(response.data) ? response.data : [];
      setMessages(items);
      setSelectedId(current => items.some(item => item.id === current) ? current : (items.find(item => !item.read)?.id || items[0]?.id || ""));
    } catch {
      toast.error("Impossible de charger la messagerie");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleMessages = useMemo(() => filter === "unread" ? messages.filter(item => !item.read) : messages, [filter, messages]);
  const selected = messages.find(item => item.id === selectedId) || null;
  const unreadCount = messages.filter(item => !item.read).length;

  const setReadState = async (message, read, silent = false) => {
    if (user?.role === "VISITOR") {
      toast.error("Mode Visiteur : le statut des messages ne peut pas être modifié");
      return;
    }
    const previous = Boolean(message.read);
    setUpdatingId(message.id);
    setMessages(items => items.map(item => item.id === message.id ? { ...item, read } : item));
    try {
      const response = await axios.put(`${API}/admin/contacts/${message.id}/read`, { read });
      const updated = { ...message, ...(response.data || {}), id: message.id, read };
      setMessages(items => items.map(item => item.id === message.id ? updated : item));
      if (!silent) toast.success(read ? "Message marqué comme lu" : "Message marqué comme non lu");
    } catch (error) {
      setMessages(items => items.map(item => item.id === message.id ? { ...item, read: previous } : item));
      if (!error?.visitorReadOnly) toast.error("Le statut du message n’a pas pu être modifié");
    } finally {
      setUpdatingId("");
    }
  };

  const selectMessage = message => {
    setSelectedId(message.id);
    if (!message.read) setReadState(message, true, true);
  };

  return <div className="space-y-5">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div><p className="mb-1 font-manrope text-[11px] font-semibold uppercase tracking-[.22em] text-sage">Pilotage</p><h1 className="font-playfair text-3xl text-charcoal">Messagerie</h1><p className="mt-1 font-manrope text-sm text-charcoal-muted">Messages reçus depuis le formulaire de contact du site.</p></div>
      <button type="button" onClick={load} disabled={loading} className="btn-secondary flex items-center gap-2"><RefreshCw size={15} className={loading ? "animate-spin" : ""}/> Actualiser</button>
    </div>

    <div className="flex w-fit items-center rounded-xl border border-sage/15 bg-white p-1" role="group" aria-label="Filtrer les messages">
      <button type="button" onClick={() => setFilter("all")} aria-pressed={filter === "all"} className={`rounded-lg px-4 py-2 text-xs font-semibold ${filter === "all" ? "bg-sage text-white" : "text-charcoal-muted"}`}>Tous ({messages.length})</button>
      <button type="button" onClick={() => setFilter("unread")} aria-pressed={filter === "unread"} className={`rounded-lg px-4 py-2 text-xs font-semibold ${filter === "unread" ? "bg-sage text-white" : "text-charcoal-muted"}`}>Non lus ({unreadCount})</button>
    </div>

    <div className="grid min-h-[560px] overflow-hidden rounded-2xl border border-sage/10 bg-white shadow-sm lg:grid-cols-[340px_minmax(0,1fr)]">
      <div className="border-b border-sage/10 lg:border-b-0 lg:border-r">
        {loading ? <div className="space-y-2 p-3">{[1,2,3].map(item => <div key={item} className="h-20 animate-pulse rounded-xl bg-bone"/>)}</div> : visibleMessages.length ? <div className="max-h-[680px] divide-y divide-sage/10 overflow-y-auto">{visibleMessages.map(message => <button key={message.id} type="button" onClick={() => selectMessage(message)} className={`grid w-full grid-cols-[20px_minmax(0,1fr)] gap-3 px-4 py-4 text-left transition-colors ${selectedId === message.id ? "bg-sage/10" : "hover:bg-bone/60"}`}><span className={`mt-0.5 ${message.read ? "text-charcoal-soft" : "text-sage"}`}>{message.read ? <MailOpen size={16}/> : <Mail size={16}/>}</span><span className="min-w-0"><span className={`block truncate text-sm ${message.read ? "font-medium text-charcoal" : "font-bold text-charcoal"}`}>{message.name || message.email}</span><span className="mt-0.5 block truncate text-xs text-charcoal-muted">{message.subject || "Sans objet"}</span><span className="mt-1 block text-[11px] text-charcoal-soft">{formatDate(message.created_at)}</span></span></button>)}</div> : <div className="flex min-h-56 flex-col items-center justify-center p-6 text-center text-charcoal-muted"><Inbox size={28} className="mb-3 text-sage"/><p className="text-sm font-medium">Aucun message</p><p className="mt-1 text-xs">Aucun message ne correspond à ce filtre.</p></div>}
      </div>

      {selected ? <article className="flex min-w-0 flex-col">
        <header className="border-b border-sage/10 p-5 lg:p-6"><div className="flex flex-wrap items-start justify-between gap-4"><div className="min-w-0"><p className="text-xs text-charcoal-muted">{formatDate(selected.created_at)}</p><h2 className="mt-1 font-playfair text-2xl text-charcoal">{selected.subject || "Sans objet"}</h2><p className="mt-2 text-sm font-semibold text-charcoal">{selected.name}</p><a href={`mailto:${selected.email}`} className="text-sm text-sage hover:underline">{selected.email}</a>{selected.phone && <a href={`tel:${selected.phone}`} className="ml-3 text-sm text-charcoal-muted hover:text-sage">{selected.phone}</a>}</div><div className="flex flex-wrap gap-2"><button type="button" onClick={() => setReadState(selected, !selected.read)} disabled={updatingId === selected.id} className="btn-secondary !px-3 !py-2 text-xs disabled:cursor-wait disabled:opacity-60">{updatingId === selected.id ? "Mise à jour…" : selected.read ? "Marquer non lu" : "Marquer lu"}</button><a href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject || "Votre message"}`)}`} className="btn-primary flex items-center gap-2 !px-3 !py-2 text-xs"><Send size={14}/> Répondre</a></div></div></header>
        <div className="flex-1 whitespace-pre-wrap p-5 font-manrope text-sm leading-7 text-charcoal lg:p-6">{selected.message}</div>
      </article> : <div className="hidden items-center justify-center text-sm text-charcoal-muted lg:flex">Sélectionnez un message pour le consulter.</div>}
    </div>
  </div>;
}
